import Cookie from 'universal-cookie';
import { DELETE, GET, POST, PUT } from '../dataSaver';

const BASE = 'user-management-service/whats-new';

const WORKSPACE_APPLICATION = {
  CAP_MANAGER: 'CAPSMART',
  MD_MANAGER: 'MDMANAGER',
  CONTRACT_MANAGER: 'CONTRACTSMART',
  PNP_MANAGER: 'PNPMANAGER',
  MED_LEARNER: 'MEDLEARNER',
  LMS_MANAGER: 'LMS',
  PROVIDER_PORTAL: 'CAPSMART',
  SALES_DEMO_PORTAL: 'CAPSMART',
};

export const isPersistedId = (id) =>
  Boolean(id) && !String(id).startsWith('page_') && !String(id).startsWith('upd_');

export const whatsNewErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const text = detail
      .map((item) => item?.msg || item?.message || (typeof item === 'string' ? item : ''))
      .filter(Boolean)
      .join(', ');
    if (text) return text;
  }
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return error?.message || fallback;
};

export const currentApplicationCode = () => {
  const workspace = sessionStorage.getItem('selectedApplication');
  return WORKSPACE_APPLICATION[workspace] || 'CAPSMART';
};

const currentEntityId = () => {
  const cookie = new Cookie();
  return cookie.get('entityId') || '';
};

const notificationToApi = (method) => {
  if (method === 'EMAIL') return ['EMAIL'];
  if (method === 'IN_APP') return ['IN_APP'];
  return ['IN_APP', 'EMAIL'];
};

const notificationFromApi = (methods = []) => {
  const values = new Set(methods || []);
  if (values.has('IN_APP') && values.has('EMAIL')) return 'IN_APP_EMAIL';
  if (values.has('EMAIL')) return 'EMAIL';
  return 'IN_APP';
};

const displayToApi = (value) => {
  if (value === 'ALWAYS') return 99;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 3;
};

const displayFromApi = (count) => {
  if (count == null) return '3';
  if (Number(count) > 3) return 'ALWAYS';
  return String(count);
};

const audienceToApi = (page) => {
  const allApplications = !page?.platform || page.platform === 'ALL';
  const allRoles = !page?.roles || page.roles === 'ALL';
  const allRegions = !page?.region || page.region === 'ALL';
  return {
    allApplications,
    applications: allApplications ? [] : [page.platform],
    userRoles: allRoles ? [] : [page.roles],
    regions: allRegions ? [] : [page.region],
  };
};

const audienceFromApi = (audience = {}) => ({
  platform:
    audience.allApplications || !audience.applications?.length
      ? 'ALL'
      : audience.applications[0],
  roles: audience.userRoles?.length ? audience.userRoles[0] : 'ALL',
  region: audience.regions?.length ? audience.regions[0] : 'ALL',
});

const linksToUi = (links = []) => ({
  guideLinks: (links || [])
    .filter((link) => link?.type === 'GUIDE' && link.url)
    .map((link) => ({
      url: link.url,
      cta: link.cta || '',
    })),
  videoLinks: (links || [])
    .filter((link) => link?.type === 'VIDEO' && link.url)
    .map((link) => ({
      url: link.url,
      cta: link.cta || 'Watch Video',
    })),
});

const linksFromUi = (update) => {
  const guides = (update?.guideLinks || []).map((item) => {
    if (typeof item === 'string') {
      return { type: 'GUIDE', cta: 'Read Guide', url: item };
    }
    return {
      type: 'GUIDE',
      cta: item?.cta || 'Read Guide',
      url: item?.url || '',
    };
  });
  const videos = (update?.videoLinks || []).map((item) => ({
    type: 'VIDEO',
    cta: item?.cta || 'Watch Video',
    url: item?.url || '',
  }));
  return [...guides, ...videos].filter((link) => link.url);
};

export const mapUpdateFromApi = (update = {}, page = null) => {
  const linkFields = linksToUi(update.links);
  const thumbnails = Array.isArray(update.thumbnails) ? update.thumbnails : [];
  const application = update.application || update.applications?.[0] || '';
  return {
    ...update,
    shortDescriptionHtml: update.description || update.shortDescriptionHtml || '',
    application,
    applications: application ? [application] : update.applications || [],
    featureSpecific: update.featureSpecific || 'GENERAL',
    guideLinks: linkFields.guideLinks,
    videoLinks: linkFields.videoLinks,
    thumbnails,
    images: thumbnails.map((item) => item.fileURL).filter(Boolean),
    selectedThumbnailId: update.selectedThumbnailId || null,
    entityIds: Array.isArray(update.entityIds) ? update.entityIds : [],
    pageTitle: update.pageTitle || page?.title || '',
    publicationDate: update.publicationDate || page?.publicationDate || '',
    createdAt: update.createdDate || update.createdAt || '',
  };
};

export const mapPageFromApi = (page = {}) => {
  const audience = audienceFromApi(page.targetAudience);
  const updates = Array.isArray(page.updates)
    ? page.updates.map((update) => mapUpdateFromApi(update, page))
    : [];
  return {
    ...page,
    ...audience,
    shortDescription: page.shortDescription || '',
    notificationMethod: notificationFromApi(page.notificationMethods),
    displayOnPublish: displayFromApi(page.displayLoginCount),
    layoutId: page.layout || page.layoutId || 'THREE_BY_THREE',
    updates,
    updateCount: page.updateCount ?? updates.length,
    entitySpecificUpdateCount:
      page.entitySpecificUpdateCount ??
      updates.filter((update) => update.entitySpecific).length,
    ctaCount:
      page.ctaCount ??
      updates.reduce(
        (sum, update) =>
          sum + (update.guideLinks?.length || 0) + (update.videoLinks?.length || 0),
        0
      ),
    createdAt: page.createdDate || page.createdAt || '',
    updatedAt: page.lastModifiedDate || page.updatedAt || '',
    publishedAt: page.publishedDate || page.publishedAt || '',
  };
};

export const pageToApi = (page) => ({
  title: page.title?.trim(),
  shortDescription: page.shortDescription?.trim(),
  publicationDate: page.publicationDate,
  targetAudience: audienceToApi(page),
  notificationMethods: notificationToApi(page.notificationMethod),
  displayLoginCount: displayToApi(page.displayOnPublish),
  layout: page.layoutId || page.layout || 'THREE_BY_THREE',
});

export const updateToApi = (update) => {
  const entitySpecific = Boolean(update.entitySpecific);
  const entityIds = entitySpecific
    ? (update.entityIds || []).map((id) => String(id).trim()).filter(Boolean)
    : [];
  if (entitySpecific && entityIds.length === 0) {
    const tenantId = currentEntityId();
    if (tenantId) entityIds.push(tenantId);
  }
  return {
    title: update.title?.trim(),
    description: update.shortDescriptionHtml || update.description || '',
    updateType: update.updateType,
    application: update.application || update.applications?.[0] || currentApplicationCode(),
    featureSpecific: update.featureSpecific || '',
    links: linksFromUi(update),
    featured: Boolean(update.featured),
    entitySpecific,
    entityIds,
  };
};

const withQuery = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    query.set(key, String(value));
  });
  const text = query.toString();
  return text ? `${path}?${text}` : path;
};

export const listWhatsNewPages = async ({ offset = 0, limit = 50 } = {}) => {
  const { data } = await GET(withQuery(`${BASE}/pages`, { offset, limit }));
  return (data?.records || []).map(mapPageFromApi);
};

export const getWhatsNewPage = async (pageId) => {
  if (!pageId) return null;
  const { data } = await GET(`${BASE}/pages/${pageId}`);
  return mapPageFromApi(data);
};

export const createWhatsNewPage = async (page) => {
  const { data } = await POST(`${BASE}/pages`, pageToApi(page));
  return mapPageFromApi(data);
};

export const updateWhatsNewPage = async (pageId, page) => {
  const { data } = await PUT(`${BASE}/pages/${pageId}`, pageToApi(page));
  return mapPageFromApi({ ...data, id: data?.id || pageId });
};

export const saveWhatsNewPage = async (page) => {
  if (isPersistedId(page?.id)) return updateWhatsNewPage(page.id, page);
  return createWhatsNewPage(page);
};

export const deleteWhatsNewPage = async (pageId) => {
  await DELETE(`${BASE}/pages/${pageId}`);
  return true;
};

export const publishWhatsNewPage = async (pageId) => {
  const { data } = await POST(`${BASE}/pages/${pageId}/publish`);
  return mapPageFromApi(data);
};

export const createWhatsNewUpdate = async (pageId, update) => {
  const { data } = await POST(`${BASE}/pages/${pageId}/updates`, updateToApi(update));
  return mapUpdateFromApi(data);
};

export const getWhatsNewUpdate = async (updateId) => {
  const { data } = await GET(`${BASE}/updates/${updateId}`);
  return mapUpdateFromApi(data);
};

export const updateWhatsNewUpdate = async (updateId, update) => {
  const { data } = await PUT(`${BASE}/updates/${updateId}`, updateToApi(update));
  return mapUpdateFromApi({ ...data, id: data?.id || updateId });
};

export const deleteWhatsNewUpdate = async (updateId) => {
  await DELETE(`${BASE}/updates/${updateId}`);
  return true;
};

export const uploadWhatsNewThumbnail = async (updateId, file) => {
  const body = new FormData();
  body.append('file', file);
  const { data } = await POST(`${BASE}/updates/${updateId}/thumbnail`, body);
  return mapUpdateFromApi({ ...data, id: data?.id || updateId });
};

export const selectWhatsNewThumbnail = async (updateId, thumbnailId) => {
  const { data } = await PUT(`${BASE}/updates/${updateId}/thumbnail/select`, { thumbnailId });
  return mapUpdateFromApi({ ...data, id: data?.id || updateId });
};

export const generateWhatsNewThumbnails = async (updateId, count = 2) => {
  const { data } = await POST(
    withQuery(`${BASE}/updates/${updateId}/thumbnails/generate`, { count })
  );
  if (data?.id || data?.thumbnails) {
    return mapUpdateFromApi({ ...data, id: data?.id || updateId });
  }
  return data;
};

export const getCurrentWhatsNew = async ({
  application = currentApplicationCode(),
  region,
} = {}) => {
  const { data } = await GET(withQuery(`${BASE}/current`, { application, region }));
  return {
    showAutomatically: Boolean(data?.showAutomatically),
    displayCount: data?.displayCount ?? 0,
    displayLimit: data?.displayLimit ?? 0,
    remainingDisplays: data?.remainingDisplays ?? 0,
    page: data?.page ? mapPageFromApi(data.page) : null,
    updates: (data?.updates || []).map((update) => mapUpdateFromApi(update, data?.page)),
  };
};

export const markWhatsNewDisplayed = async (pageId) => {
  const { data } = await POST(`${BASE}/pages/${pageId}/displayed`);
  return data;
};

export const getWhatsNewPageView = async (
  pageId,
  { application = currentApplicationCode(), region } = {}
) => {
  const { data } = await GET(withQuery(`${BASE}/pages/${pageId}/view`, { application, region }));
  return mapPageFromApi(data);
};

export const listWhatsNewUpdates = async ({
  application,
  search,
  updateType,
  category,
  contentType,
  region,
  sortDirection = 'DESC',
  offset = 0,
  limit = 20,
} = {}) => {
  const { data } = await GET(
    withQuery(`${BASE}/updates`, {
      application,
      search: search?.trim(),
      updateType,
      category,
      contentType,
      region,
      sortDirection,
      offset,
      limit,
    })
  );
  return {
    records: (data?.records || []).map((update) => mapUpdateFromApi(update)),
    total: data?.total ?? 0,
    offset: data?.offset ?? offset,
    limit: data?.limit ?? limit,
  };
};
